import { useMemo } from 'react';
import type { Setup } from '@/types';
import type { UseTuningSession } from '../hooks/useTuningSession';
import { getTuningSpec } from '../data/bow-tuning-specs';
import { BOWS } from '@/data/bows';
import { Card } from '@/components/ui/Card';
import { GoalCard, TipCard, WarningCard, InfoCard } from '../components/Cards';

interface CenterShotData {
  /** Measured distance from riser to center of arrow shaft (inches). */
  measured?: number;
  /** Has the user used a center-shot tool / laser / arrow-on-string-and-cam check? */
  measurementMethod?: 'eyeball' | 'square' | 'laser' | 'string-cam-align';
}

interface Props {
  tuning: UseTuningSession;
  setup: Setup;
}

/**
 * Step 4 — Center Shot
 *
 * Initial alignment of the rest so the arrow points cleanly through the Berger
 * hole on the line of force from the bow's center. Most modern hunting bows
 * spec 13/16" from riser to center of arrow shaft as the starting point.
 *
 * This is a STARTING point — paper tune (step 7) micro-adjusts from here.
 */
export function CenterShotStep({ tuning }: Props) {
  const preflight = (tuning.session.stepData['preflight'] as
    | { bowBrand?: string; bowModel?: string; bowYear?: number }
    | undefined) ?? {};
  const data = (tuning.session.stepData['center_shot'] as CenterShotData | undefined) ?? {};
  const update = (patch: Partial<CenterShotData>) =>
    tuning.updateStepData<CenterShotData>('center_shot', { ...data, ...patch });

  const bowEntry = useMemo(
    () =>
      preflight.bowBrand && preflight.bowModel && preflight.bowYear
        ? BOWS.find(
            (b) =>
              b.brand === preflight.bowBrand &&
              b.model === preflight.bowModel &&
              b.year === preflight.bowYear,
          )
        : undefined,
    [preflight.bowBrand, preflight.bowModel, preflight.bowYear],
  );
  const tuningSpec = useMemo(
    () => (bowEntry ? getTuningSpec(bowEntry.brand, bowEntry.model, bowEntry.year) : null),
    [bowEntry],
  );

  const spec = tuningSpec?.stockCenterShot ?? 0.8125;
  const tolerance = tuningSpec?.centerShotTolerance ?? 0.0625;
  const delta =
    data.measured !== undefined ? Math.abs(data.measured - spec) : null;
  const inSpec = delta !== null && delta <= tolerance;
  const overSpec = delta !== null && delta > tolerance && data.measured! > spec;
  const underSpec = delta !== null && delta > tolerance && data.measured! < spec;

  return (
    <>
      <GoalCard title="What we're trying to achieve">
        Position the rest so the arrow tip aligns with the bow's center line (the Berger
        hole, on the centerline of the riser). Most modern compounds spec{' '}
        <strong>{formatFrac(spec)}</strong> from the riser to the center of the arrow shaft
        as a starting point. We'll micro-adjust during paper tune.
      </GoalCard>

      {bowEntry && tuningSpec && (
        <Card padding="md">
          <p className="text-[11px] font-mono uppercase tracking-wider text-zinc-500 mb-1">
            Your bow's center-shot spec
          </p>
          <p className="text-sm">
            <strong className="text-zinc-100">
              {bowEntry.brand} {bowEntry.model}
            </strong>{' '}
            — manufacturer stock: <strong>{formatFrac(spec)}</strong> (±{formatFrac(tolerance)})
          </p>
        </Card>
      )}

      <Card padding="lg">
        <h3 className="font-semibold mb-3">Measure your center shot</h3>
        <p className="text-[12px] text-zinc-400 mb-4 leading-relaxed">
          Place a tape measure or a center-shot tool against the inside face of the riser.
          Measure to the center of the arrow shaft (NOT the rest body). Don't measure to
          the edge of the shaft — center matters because rest contact is centerline.
        </p>

        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Riser → center of shaft (in)">
            <input
              type="number"
              step={0.03125}
              value={data.measured ?? ''}
              onChange={(e) => update({ measured: parseFloatOrUndef(e.target.value) })}
              placeholder={formatFrac(spec)}
              className={inputClass}
            />
          </Field>
          <Field label="Measurement method">
            <select
              value={data.measurementMethod ?? ''}
              onChange={(e) =>
                update({
                  measurementMethod:
                    (e.target.value as CenterShotData['measurementMethod']) || undefined,
                })
              }
              className={inputClass}
            >
              <option value="">How did you measure?</option>
              <option value="square">Bow square / center-shot gauge (best)</option>
              <option value="laser">Laser center-shot tool (best)</option>
              <option value="string-cam-align">Lined up nock + string + bottom cam by eye</option>
              <option value="eyeball">Eyeballed it with a ruler</option>
            </select>
          </Field>
        </div>

        {data.measured !== undefined && (
          <div className="mt-4 grid sm:grid-cols-3 gap-3">
            <Readout label="Your measurement" value={formatFrac(data.measured)} />
            <Readout label="Spec" value={formatFrac(spec)} />
            <Readout
              label="Delta"
              value={
                delta === 0 || delta === null
                  ? '0'
                  : `${overSpec ? '+' : '-'}${formatFrac(delta)}`
              }
              color={inSpec ? 'emerald' : 'rose'}
            />
          </div>
        )}
      </Card>

      {inSpec && (
        <div className="rounded-lg border border-emerald-700/40 bg-emerald-950/20 p-3 flex gap-2.5">
          <div className="text-emerald-400 text-lg">✓</div>
          <div className="text-sm">
            <p className="font-semibold text-emerald-300">Center shot is in spec.</p>
            <p className="text-zinc-300 mt-0.5">
              Move on to nocking point. Paper tune will fine-tune from here — small
              micro-adjustments to L/R based on the tear direction.
            </p>
          </div>
        </div>
      )}

      {(overSpec || underSpec) && (
        <TipCard
          title={`Fix: rest is ${overSpec ? 'too far OUT from riser' : 'too close to riser'}`}
          defaultOpen
        >
          <p className="mb-2">
            Adjust the L/R micro-adjustment on your rest body. Most modern rests (Hamskea,
            QAD, Vapor Trail) have a numbered windage scale + a thumb-screw or hex bolt
            for fine adjustment.
          </p>
          <ol className="list-decimal list-inside space-y-1 mb-3 text-zinc-300">
            <li>
              Loosen the rest's windage lock (usually a small Allen bolt on the side of the
              rest body).
            </li>
            <li>
              Slide the rest{' '}
              {overSpec ? 'TOWARD the riser (inward)' : 'AWAY from the riser (outward)'}{' '}
              by approximately {delta !== null ? formatFrac(delta) : '—'}.
            </li>
            <li>Retighten the windage lock.</li>
            <li>Re-measure with your bow square / laser tool.</li>
          </ol>
          <p className="text-[11px] text-zinc-400">
            One full turn of most rest micro-adjust screws ≈ 1/32" of windage. Plenty of
            range to land on spec exactly.
          </p>
        </TipCard>
      )}

      {data.measurementMethod === 'eyeball' && (
        <TipCard title="Eyeballing isn't precise enough for tuning">
          <p className="mb-2">
            Center shot at the 1/32" level matters for paper tune to converge. Pick up a
            simple bow square ($15-$25 at any pro shop or Amazon) or a laser center-shot
            tool — and your tuning becomes 10x faster.
          </p>
        </TipCard>
      )}

      <InfoCard title="What is center shot?">
        <p className="mb-2">
          Center shot is the position of the arrow rest left-to-right such that the
          arrow points cleanly through the line of force as the bow fires. Get it wrong
          and the cams launch the arrow at a slight angle to the rest — instant paper
          tear, no matter what else you do.
        </p>
        <p className="mb-2">
          Why 13/16"? On most modern hunting bows the cam stack is offset slightly from
          the riser centerline; 13/16" puts the arrow shaft on the actual line of force.
          Your bow's manufacturer tune chart is the authoritative source — that's what
          we're showing in the spec card.
        </p>
        <p>
          <strong>Paper tune adjusts from here.</strong> Don't sweat being a few
          thousandths off after this step — paper tune is where the real precision happens.
          This is just a clean starting point.
        </p>
      </InfoCard>

      <WarningCard title="Safety reminder">
        Always test-shoot at close range (5 yards into a sturdy backstop) before stepping
        back to 20+ yards after any rest adjustment. A miscalibrated rest can launch an
        arrow into your stabilizer, riser, or worse.
      </WarningCard>
    </>
  );
}

// ─── helpers ───

const inputClass =
  'w-full bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-ember-500';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-[11px] text-zinc-500 block mb-1">{label}</label>
      {children}
    </div>
  );
}

function Readout({ label, value, color }: { label: string; value: string; color?: 'emerald' | 'rose' }) {
  return (
    <div
      className={
        'rounded-md border p-2.5 ' +
        (color === 'emerald'
          ? 'border-emerald-700/40 bg-emerald-950/20'
          : color === 'rose'
            ? 'border-rose-700/40 bg-rose-950/20'
            : 'border-zinc-800 bg-zinc-950/50')
      }
    >
      <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-0.5">
        {label}
      </p>
      <p
        className={
          'text-sm font-semibold ' +
          (color === 'emerald'
            ? 'text-emerald-300'
            : color === 'rose'
              ? 'text-rose-300'
              : 'text-zinc-100')
        }
      >
        {value}
      </p>
    </div>
  );
}

function parseFloatOrUndef(s: string): number | undefined {
  if (s === '') return undefined;
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : undefined;
}

function formatFrac(inches: number): string {
  // Express as 13/16", 3/4", etc.
  const sixteenths = Math.round(inches * 16);
  if (sixteenths === 0) return '0"';
  if (sixteenths % 16 === 0) return `${sixteenths / 16}"`;
  const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));
  const g = gcd(sixteenths, 16);
  return `${sixteenths / g}/${16 / g}"`;
}
